// @ts-strict-ignore
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import { CallEvents, CallMedia, CallRB, CallStatus, CallType } from "rainbow-web-sdk/models/call.model";
import anonymizer from 'rainbow-web-sdk/utils/anonymizer';
import { WebrtcConfigAndUtils } from "rainbow-web-sdk/services/webrtc/webrtcConfigAndUtils";
import type { WebrtcP2PService } from "rainbow-web-sdk/services/webrtc/webrtcP2P.service";
import { WebrtcP2PServiceEvents } from "rainbow-web-sdk/services/webrtc/webrtcP2P.service";
import xmlParser, { XMLElement } from 'rainbow-web-sdk/utils/xmlParser';
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { SettingsService } from 'rainbow-web-sdk/services/core';
import { Subscription } from 'rxjs';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { ConversationServiceRB } from '../conversation/conversation.service';
import { $msg } from 'strophe.js';
import { JingleSession } from '../../libs/strophe/strophe.jingle.session';
import { WebrtcStatisticsData } from './webrtcSessionStatsHandler';
import { stringifyError } from '../../utils/stringifyError';

export class WebrtcP2PEventHandler {

    private xmppService: XmppService = XmppService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private eventService: EventService = EventService.getInstance();
    private webrtcConfigAndUtils: WebrtcConfigAndUtils = WebrtcConfigAndUtils.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();
    private conversationService: ConversationServiceRB = ConversationServiceRB.getInstance();
    private nameSpace = "urn:xmpp:jingle-message:0";
    private messageHandlerRef;
    private xmppServiceSubscription: Subscription;

    constructor(private webrtcP2PService: WebrtcP2PService) {
        this.xmppServiceSubscription = this.xmppService.connection.jingle.rxSubject.subscribe((evt: RBEvent) => { this.onJingleMessage(evt); });

        this.messageHandlerRef = this.xmppService.addHandler((stanza) => {
            try {
                this.onMessageReceived(stanza); return true;
            } catch (err) {
                this.logger.error("[WebrtcP2PEventHandler] caught error " + err);
                return true;
            }
        }, this.nameSpace, "message");
    }

    public destroy() {
        this.xmppServiceSubscription?.unsubscribe();
        if (this.messageHandlerRef) {
            this.xmppService.deleteHandler(this.messageHandlerRef);
            this.messageHandlerRef = null;
        }
    }

    private onJingleMessage(event: RBEvent): void {
        try {
            switch (event.name) {
                case 'jingleSessionInitiate': this.onJingleSessionInitiate(event.data.sid); break;
                case 'jingleConnectionStateChange': this.onSessionConnectionStateUpdate(event.data.sid, event.data.session); break;
                case 'jingleSessionTerminate': this.terminatedCall(event.data.sid, event.data.reason); break;
                case 'webrtcStatsEvent.jingle': this.onJingleWebrtcStatsEvent(event.data.sid, event.data.data); break;
                case 'jingleError': this.onJingleError(event.data.sid, event.data.error); break;
                case 'jingleTransportReplace': this.onJingleTransportReplace(event.data.sid); break;
                case 'jingleSessionAccept': this.onJingleCallAccepted(event.data.sid); break;
                case 'jingleSessionInfoHold': this.onJingleHoldEvent(event.data.sid); break;
                case 'jingleSessionInfoUnhold': this.onJingleUnholdEvent(event.data.sid); break;
                case 'jingleMediaModified': this.mediaModified(event.data.sid); break;
                default:
                    break;
            }
        }
        catch (error) {
            this.logger.info(`[WebrtcP2PEventHandler] onJingleMessage -- failure -- ${event.name} ${stringifyError(error)}`);
        }
    }

    // **********************************************************
    //  HELPER FUNCTIONS
    // **********************************************************

    private onJingleSessionInitiate(sessId) {
        this.logger.info("[WebrtcP2PEventHandler] onJingleSessionInitiate " + sessId);

        const session = this.xmppService.connection.jingle.sessions[sessId];
        if (session.localType === 'application' || session.remoteType === 'application') return;

        // Check if the session is related to a web conference
        if (session?.confId) { /* Do nothing */ }
        else if (session.replaceSid) {
            //check if this is case of shift call
            const call = this.webrtcP2PService?.calls[session.replaceSid];
            if (!call) {
                //we want to replace a call that does not exist, reject incoming session init
                this.logger.warn("[WebrtcP2PEventHandler] onJingleSessionInitiate -- reject as replace call does not exist" + session.replaceSid);

                this.xmppService.connection.jingle.terminate(sessId, session.callShift ? "expired" : "nosession");
                return;
            }

            if (session.callShift) {
                this.logger.info(`[WebrtcP2PEventHandler]  | callincoming.jingle -- callShift call case for ${session.replaceSid} to ${sessId}`);
                this.onIncomingShiftCall(session.replaceSid, sessId);
            }
            else {
                //webrtc to webrtc case
                this.logger.info("[WebrtcP2PEventHandler]  | callincoming.jingle -- webrtc to webrtc transfer call case");
                if (call) {
                    this.webrtcP2PService.removeCallObject(call);
                }
                this.onIncomingTrasferredCall(sessId);
            }
        }
        else {
            this.incomingCall(sessId);
        }
    }

    private onJingleTransportReplace(callId: string) {
        const sess = this.xmppService.connection.jingle.sessions[callId];
        if (sess) {
            this.logger.info("[WebrtcP2PEventHandler]  | jingleTransportReplace " + callId);
            sess.sendAnswer("transport-accept");
        } else {
            this.logger.warn("[WebrtcP2PEventHandler]  | jingleTransportReplace no such session !");
        }
    }

    private onJingleWebrtcStatsEvent(sid, data: WebrtcStatisticsData) {
        //update network quality
        const call = this.webrtcP2PService?.calls[sid];
        if (call && data.audio?.received) {
            if (data.audio.received.networkQualityValue === call.networkQuality) {
                call.networkQualityIteration = call.networkQualityIteration ? (call.networkQualityIteration + 1) : 1;
                if (call.networkQuality === 0 && call.networkQualityIteration >= 3) {
                    call.sendEvent(CallEvents.ON_CALL_NETWORK_BAD_QUALITY, call.networkQuality);
                }
            }
            else {
                call.networkQualityIteration = 1;
            }

            if (call.networkQuality !== data.audio.received.networkQualityValue) {
                call.networkQuality = data.audio.received.networkQualityValue;
                call.sendEvent(CallEvents.ON_CALL_NETWORK_QUALITY_CHANGE, call.networkQuality);
            }
        }
    }

    private onJingleHoldEvent(callId: string) {
        const session = this.xmppService.connection.jingle.sessions[callId];
        if (!session) {
            this.logger.info("[WebrtcP2PEventHandler] | hold - received on a non existing session !");
            return;
        }

        this.logger.info("[WebrtcP2PEventHandler] | hold " + callId);
        const call = this.webrtcP2PService?.calls[callId];
        if (!call?.isMediaPillarCall()) {
            // WEBRTC usecase
            // mute audio to stop sending media
            session.muteAudio(true);

            if (call) {
                call.putOnHold = true;
                call.setStatus(CallStatus.PUT_ON_HOLD);
                this.webrtcP2PService.sendUpdateCallEvent(call);
            }
        } else {
            // WEBRTC GW usecase
            const options = {
                iceRestart: false
            };

            if (this.settingsService.getBooleanSetting("enableIceRestartOnHold")) {
                options.iceRestart = true;
            }

            if (session.peerconnection.getTransceivers?.().length) {
                //put all transceivers to sendonly
                session.peerconnection.getTransceivers().forEach(function (transceiver) {
                    if (transceiver.direction !== "recvonly") { transceiver.direction = "sendonly"; }
                });
            }
            else {
                options["offerToReceiveAudio"] = false;

                //save reconenction options, call is on hold
                session.reconnectionOptions = {
                    iceRestart: true,
                    offerToReceiveAudio: false
                };
            }

            session.contentModify("audio", options);
        }

        this.webrtcP2PService.sendEvent(WebrtcP2PServiceEvents.RAINBOW_ON_CALL_UPDATED_EVENT);
    }

    private async onJingleUnholdEvent(callId: string) {
        const session = this.xmppService.connection.jingle.sessions[callId];
        if (!session) {
            this.logger.info("[WebrtcP2PEventHandler] | unhold - received on a non existing session !");
            return;
        }

        this.logger.info("[WebrtcP2PEventHandler] | unhold " + callId);
        // unmute audio
        const call = this.webrtcP2PService?.calls[callId];
        if (!call?.isMediaPillarCall()) {
            // WEBRTC usecase
            // unmute audio
            session.muteAudio(false);

            if (call) {
                call.putOnHold = false;
                session.muteAudio(call.isMuted);
                call.setStatus(CallStatus.ACTIVE);
                this.webrtcP2PService.sendUpdateCallEvent(call);
                this.webrtcP2PService.attachDistantMediaStreams(true, call);
            }
        } else {
            // WEBRTC GW usecase
            const options = {
                iceRestart: false
            };

            if (this.settingsService.getBooleanSetting("enableIceRestartOnUnhold")) {
                options.iceRestart = true;
            }

            if (session.peerconnection.getTransceivers?.().length) {
                //reactivate all transceivers
                session.peerconnection.getTransceivers().forEach(function (transceiver) {
                    if (transceiver.direction !== "recvonly") { transceiver.direction = "sendrecv"; }
                });
            }
            else {
                options["offerToReceiveAudio"] = true;

                //remove all reconenction options, call is active
                session.reconnectionOptions = null;
            }
            await session.contentModify("audio", options);
        }

        this.webrtcP2PService.sendEvent(WebrtcP2PServiceEvents.RAINBOW_ON_CALL_UPDATED_EVENT);
    }

    private onJingleCallAccepted(callId: string) {
        const call = this.webrtcP2PService?.calls[callId];
        const sess = this.xmppService.connection.jingle.sessions[callId];

        if (call && sess) {
            this.logger.info("[WebrtcP2PEventHandler] | callaccepted " + callId);
            //start webrtc stats
            if (!call.statsInterval) {
                this.webrtcConfigAndUtils.createOrUpdateStatisticsForCall(call);
                this.logger.info("[WebrtcP2PEventHandler] | start getStats interval");
                call.statsInterval = this.webrtcConfigAndUtils.getStatsForSession(sess, 5000);
            }
        }
    }

    public setRemoteTypeMedia(remoteType: string[], call: CallRB): void {
        call.remoteMedia = 0;
        remoteType.forEach((type: string) => {
            switch (type) {
                case "audio": call.remoteMedia = call.remoteMedia | CallMedia.AUDIO; break;
                case "video": call.remoteMedia = call.remoteMedia | CallMedia.VIDEO; break;
                case "sharing": call.remoteMedia = call.remoteMedia | CallMedia.SHARING; break;
                default: call.remoteMedia = null; break;
            }
        });
        this.logger.debug("[WebrtcP2PEventHandler] REMOTE TYPE || REMOTE MEDIA  " + call.remoteMedia);
    }

    public setLocalTypeMedia(localType: string[], call: CallRB): void {
        call.localMedia = 0;
        localType.forEach((type: string) => {
            switch (type) {
                case "audio": call.localMedia = call.localMedia | CallMedia.AUDIO; break;
                case "video": call.localMedia = call.localMedia | CallMedia.VIDEO; break;
                case "sharing": call.localMedia = call.localMedia | CallMedia.SHARING; break;
                default: call.localMedia = null; break;
            }
        });
        this.logger.debug("[WebrtcP2PEventHandler] LOCAL TYPE || LOCAL MEDIA  " + call.localMedia);
    }

    // **********************************************************
    //  XMPP XEP 0353 - JINGLE MULTI CLIENT ACTIONS
    // **********************************************************
    public async sendProposition(contact: Contact, media: string, message: string = "", addAudioToSharingCall: boolean = false): Promise<CallRB> {

        this.logger.info("[WebrtcP2PEventHandler] | sendProposition to " + JSON.stringify({ displayName: contact.nameForLogs, resources: contact.resources }));

        let mediaToGet = ["audio"];
        let secondMediaToGet = [];
        switch (media) {
            case 'video': mediaToGet.push("video"); break;
            case 'videoSharing': mediaToGet.push("video"); secondMediaToGet = ["sharing"]; break;
            case 'videoOnly': mediaToGet = ["video"]; break;
            case 'sharing': mediaToGet = ["audio"]; secondMediaToGet = ["sharing"]; break;
            case 'sharingOnly': mediaToGet = ["sharing"]; break;
            default: break;
        }

        const id = "web_" + crypto.randomUUID();

        // Create the call object
        const call = CallRB.create(CallStatus.ENDED, id, CallType.WEBRTC, contact);
        let stream: any;
        let extraStream: any;

        try {
            stream = await this.webrtcConfigAndUtils.getBrowserMedia(mediaToGet, undefined, undefined, undefined, call);
            extraStream = await this.webrtcConfigAndUtils.getBrowserMedia(secondMediaToGet, undefined, undefined, undefined, undefined, addAudioToSharingCall);

            //Check if the call still should be done !
            if (this.webrtcP2PService.makingCall && this.webrtcP2PService.started) {
                this.webrtcP2PService.calls[call.id] = call;
                call.isInitiator = true;
                stream.callId = id;

                if (this.webrtcP2PService.speechEnhancementService && (this.settingsService.getSetting('speechEnhancementType') !== "none" || this.settingsService.getSetting("enableSpeechEnhancement") === "true") && stream && stream.getAudioTracks().length) {
                    stream = await this.webrtcP2PService.speechEnhancementService.startProcessStream(call.id, stream, "[WebrtcP2PEventHandler] sendProposition");
                }

                let modifiedStream = stream;
                if (mediaToGet.indexOf("video") !== -1) {
                    call["originalStream"] = stream;
                    if (!this.webrtcP2PService.virtualBackgroundService) modifiedStream = stream;
                    else modifiedStream = await this.webrtcP2PService.virtualBackgroundService.startProcessStream(stream);
                }

                this.webrtcP2PService.pushInLocalStreams(modifiedStream, id);

                if (extraStream) {
                    extraStream.callId = id;
                    this.webrtcP2PService.pushInLocalStreams(extraStream, id);
                    mediaToGet.push(secondMediaToGet.pop());
                }

                this.setLocalTypeMedia(mediaToGet, call);

                const idPropose = "web_" + crypto.randomUUID();
                let xmppMessage = $msg({ from: this.contactService.userContact.fullJid, to: contact.jid, id: idPropose });
                xmppMessage.c("propose", { xmlns: this.nameSpace, id: id });
                if (message) {
                    xmppMessage.c("subject", { xmlns: "urn:xmpp:jingle-subject:0" }).t(message).up();
                }
                xmppMessage.c("description", { xmlns: "urn:xmpp:jingle:apps:rtp:1", media: mediaToGet[0] }).up();

                if (mediaToGet[1]) {
                    xmppMessage = xmppMessage.c("description", { xmlns: "urn:xmpp:jingle:apps:rtp:1", media: mediaToGet[1] }).up();
                }

                xmppMessage = xmppMessage.c("unifiedplan", { xmlns: "urn:xmpp:jingle:apps:jsep:1" }).up();

                this.xmppService.send(xmppMessage);

                if (call.localMedia === CallMedia.SHARING && call.contact && this.webrtcP2PService.hasMediaPillarCallWithContactJid(call.contact.jid)) {
                    //we're in MP call with the same contact, mark this call as webrtc call on top of PBX call
                    call.isSecondSharingCall = true;
                }

                call.setStatus(CallStatus.DIALING);

                //call is already made
                this.webrtcP2PService.makingCall = false;

                // Update presence
                this.contactService.setBusyState("dnd", this.webrtcP2PService.calculatePresenceMessage(call));

                this.webrtcP2PService.sendUpdateCallEvent(call);

                // Disable autoReleaseTimeout
                if (this.webrtcP2PService.autoreleaseTimeout) {
                    clearTimeout(this.webrtcP2PService.autoreleaseTimeout);
                }

                //set autoreleaseTimeout
                this.webrtcP2PService.autoreleaseTimeout = setTimeout(() => {
                    this.logger.info("[WebrtcP2PEventHandler]  | send proposition : no response after 30 seconds : release the call");
                    if (call) {
                        this.webrtcP2PService.terminateCall(call);
                    }
                }, 30000);

                this.webrtcP2PService.sendEvent(WebrtcP2PServiceEvents.RAINBOW_ON_CALL_UPDATED_EVENT, { callId: id, contact: contact });
            } else {
                this.logger.warn("[WebrtcP2PEventHandler] | sendProposition -- call should no longer be made !");

                this.webrtcP2PService.makingCall = false;

                // We should stop this call and free all streams
                stream.getTracks().forEach((track) => { track.enabled = false; track.stop(); track = null; });
                if (stream.stop) stream.stop();
                stream = null;

                if (extraStream) {
                    extraStream.getTracks().forEach((track) => { track.enabled = false; track.stop(); track = null; });
                    if (extraStream.stop) extraStream.stop();
                    extraStream = null;
                }
            }
        }
        catch (error) {
            this.logger.info("[WebrtcP2PEventHandler]   | sendProposition failure : " + JSON.stringify({ error: error.message }));

            // call is already made
            this.webrtcP2PService.makingCall = false;

            if (secondMediaToGet.indexOf("sharing") === -1 && error.toString().indexOf("getBrowserMedia") !== -1) {
                this.webrtcP2PService.openErrorPopup();
            }
            this.webrtcP2PService.removeCallObject(call);

            if (!this.webrtcP2PService.isUserContactInCall()) {
                this.webrtcP2PService.resetToSafeState();
            }

            if (this.webrtcP2PService.autoreleaseTimeout) {
                clearTimeout(this.webrtcP2PService.autoreleaseTimeout);
            }

            if (stream) {
                stream.getTracks().forEach((track) => { track.enabled = false; track.stop(); track = null; });
                if (stream.stop) stream.stop();
                stream = null;
            }

            if (extraStream) {
                extraStream.getTracks().forEach((track) => { track.enabled = false; track.stop(); track = null; });
                if (extraStream.stop) extraStream.stop();
                extraStream = null;
            }

            throw error;
        }
        return call;
    }

    public retractProposition(call) {
        this.logger.info("[WebrtcP2PEventHandler]   | retractProposition for call " + call.id);

        const idRetract = "web_" + crypto.randomUUID();
        const xmppMessage = $msg({ from: this.contactService.userContact.fullJid, to: call.contact.jid, id: idRetract })
            .c("retract", { xmlns: this.nameSpace, id: call.id });

        this.xmppService.send(xmppMessage);

        this.eventService.publish("CHECK_AND_RESET_BUSY_STATE");

        this.webrtcP2PService.removeCallObject(call);
    }

    public acceptProposition(call, type) {
        if (call.callStatus === CallStatus.ANSWERING || call.callStatus === CallStatus.ACTIVE || call.callStatus === CallStatus.CONNECTING) {
            this.logger.warn("[WebrtcP2PEventHandler] | acceptProposition for call " + call.id + " -- ignored! Already answering");
            //ignore
            return;
        }

        this.logger.info("[WebrtcP2PEventHandler] | acceptProposition for call " + call.id);
        //put in answering state
        call.setStatus(CallStatus.ANSWERING);

        //send accept to my ressources
        const idAccept = "web_" + crypto.randomUUID();
        let xmppMessage = $msg({ from: this.contactService.userContact.fullJid, to: this.contactService.userContact.jid, id: idAccept })
            .c("accept", { xmlns: this.nameSpace, id: call.id });

        this.xmppService.send(xmppMessage);

        const idProceed = "web_" + crypto.randomUUID();
        //send proceed to other participant full jid
        xmppMessage = $msg({ from: this.contactService.userContact.fullJid, to: call.fullJid, id: idProceed })
            .c("proceed", { xmlns: this.nameSpace, id: call.id });

        xmppMessage = xmppMessage.c("unifiedplan", { xmlns: "urn:xmpp:jingle:apps:jsep:1" }).up();

        this.xmppService.send(xmppMessage);

        //update local type media
        let mediaToGet = ["audio"];

        if (type && type === "video") {
            mediaToGet.push("video");
        }

        //video only call
        if (call.remoteMedia === CallMedia.VIDEO) {
            mediaToGet = ["video"];
        }

        //sharing w/o audio
        if (call.remoteMedia === CallMedia.SHARING) {
            mediaToGet = [];
        }

        this.setLocalTypeMedia(mediaToGet, call);

        this.contactService.setBusyState("dnd", this.webrtcP2PService.calculatePresenceMessage(call));

        this.webrtcP2PService.sendUpdateCallEvent(call);

        if (this.webrtcP2PService.autoreleaseTimeout) {
            clearTimeout(this.webrtcP2PService.autoreleaseTimeout);
        }

        this.webrtcP2PService.autoreleaseTimeout = setTimeout(() => {
            if (call) {
                if (call.getMediaPillarCall() !== null) {
                    this.logger.warn("[webrtcgateway/WebrtcP2PEventHandler] | acceptProposition autoreleaseTimeout after 45 s : remove the call :" + call.id);
                }
                else {
                    this.logger.info("[WebrtcP2PEventHandler] | acceptProposition autoreleaseTimeout after 45 s : remove the call " + call.id);
                }
                this.webrtcP2PService.removeCallObject(call);
                this.eventService.publish("CHECK_AND_RESET_BUSY_STATE");
            }
        }, 45000);
    }

    public rejectProposition(call: CallRB, __cause: string = "", notifyOtherParticipant: boolean = true) {
        this.logger.info("[WebrtcP2PEventHandler] | rejectProposition for call " + call.id);

        // Send reject to my ressources
        const idRejectToMe = "web_" + crypto.randomUUID();
        let xmppMessage = $msg({ from: this.contactService.userContact.fullJid, to: this.contactService.userContact.jid, id: idRejectToMe })
            .c("reject", { xmlns: this.nameSpace, id: call.id });
        this.xmppService.send(xmppMessage);

        if (notifyOtherParticipant) {
            // Send reject to other participant
            const mediaPillarContext = call.getMediaPillarCall();
            const otherJid = (mediaPillarContext?.getMediaPillarJid()) ? mediaPillarContext.getMediaPillarJid() : call.contact.jid;
            const idRejectToDistant = "web_" + crypto.randomUUID();
            const rejectContent = __cause ? { xmlns: this.nameSpace, id: call.id, cause: __cause } : { xmlns: this.nameSpace, id: call.id };//RQRAINB-9062 introduce cause
            if (otherJid) {
                xmppMessage = $msg({ from: this.contactService.userContact.fullJid, to: otherJid, id: idRejectToDistant })
                    .c("reject", rejectContent);
                this.xmppService.send(xmppMessage);
            }
            else {
                this.logger.error("[WebrtcP2PEventHandler] | rejectProposition -- missing other JID !");
            }
        }

        //remove call object from current device
        //this.webrtcP2PService.removeCallObject(call); //THIS ACTION RELEASE WRONGLY THE CURRENT Media
        // Instead Update call object
        if (call) {
            this.webrtcP2PService.removeCallObject(call);
            this.eventService.publish("CHECK_AND_RESET_BUSY_STATE");
        }

        //set the webrtc configuration
        this.webrtcConfigAndUtils.setWebrtcConfiguration();

        if (this.webrtcP2PService.autoreleaseTimeout) {
            clearTimeout(this.webrtcP2PService.autoreleaseTimeout);
        }
    }

    //RQRAINB-9057++ in case of reserved mode (stop ringing but keep the call) we have to notify others clients of a reject
    //with specific cause (reserved) in order they stop to manage the call as for a reject but loccaly the call is not released !
    public notifyRemoteOfDummyRejectProposition(callId: string, __cause: string = ""): void {
        this.logger.info("[WebrtcP2PEventHandler] | notifyRemoteOfDummyRejectProposition for call Id " + callId);
        // Send reject to my ressources but locally the call will be maintained

        const idRejectToMe = "web_" + crypto.randomUUID();
        const rejectContent = __cause ? { xmlns: this.nameSpace, id: callId, cause: __cause } : { xmlns: this.nameSpace, id: callId };
        const xmppMessage = $msg({ from: this.contactService.userContact.fullJid, to: this.contactService.userContact.jid, id: idRejectToMe })
            .c("reject", rejectContent);
        this.xmppService.send(xmppMessage);
    }//RQRAINB-9057--

    // **********************************************************
    //  XMPP XEP 0353 - JINGLE MULTI CLIENT EVENT FUNCTIONS
    // **********************************************************

    // All messages are received here, this function is used as a distributor
    public onMessageReceived(stanza: any): boolean {
        try {
            const stanzaElem = xmlParser(stanza);
            const proposeNode = stanzaElem.find("propose");
            const conferenceNode = proposeNode.find("conference");
            const conferenceSessionId = conferenceNode.attr("id");

            if (conferenceSessionId) {
                //ignore conference incoming calls for propose
                return true;
            }

            if (stanzaElem.find("error").length || stanzaElem.find("delay").length || stanzaElem.find("service-unavailable").length) this.onMessageReceivedError(stanza);
            else if (stanzaElem.find("propose").length) this.onPropositionReceived(stanza);
            else if (stanzaElem.find("retract").length) this.onRetractReceived(stanza);
            else if (stanzaElem.find("reject").length) this.onRejectReceived(stanza);
            else if (stanzaElem.find("accept").length) this.onAcceptReceived(stanza);
            else if (stanzaElem.find("proceed").length) this.onProceedReceived(stanza);
        }
        catch (error) {
            this.logger.error("[WebrtcP2PEventHandler]   | onMessageReceived error " + error);
        }
        return true;
    }

    private onMessageReceivedError(stanza: any): void {
        this.logger.info("[WebrtcP2PEventHandler]   | onMessageReceivedError");

        const id = xmlParser(stanza).find("propose").attr("id");

        const call = this.webrtcP2PService.calls[id];

        //if call is still in DIALING state, stop it
        if (call) {
            this.logger.error("[WebrtcP2PEventHandler]   | onMessageReceivedError call is in " + call.callStatus + " state");
            // this.retractProposition(call);
        }
    }

    private async getOrCreateContact(jid: string, stanzaElem: XMLElement): Promise<Contact> {
        if (this.contactService.isMediaPillarJid(jid)) {
            this.logger.info("[WebrtcP2PEventHandler] | getOrCreateContact for mediapillar jid " + jid);
            const mediapillarNode = stanzaElem.find("mediapillar");
            if (mediapillarNode.length > 0) {
                const callernumberNode = mediapillarNode.find("callernumber");
                const callernumber = callernumberNode ? callernumberNode.text() : undefined;
                const displaynameNode = mediapillarNode.find("displayname");
                const displayname = displaynameNode ? displaynameNode.text() : undefined;
                this.logger.info("[WebrtcP2PEventHandler]   | getOrCreateContact MP for jid " + jid + " callernumber: " + anonymizer(callernumber) + " displayname: " + anonymizer(displayname));

                const foundContactByNumber = this.contactService.getContactFromCacheContainsPhoneNumber(callernumber);
                if (foundContactByNumber && (foundContactByNumber !== this.contactService.userContact)) {
                    this.logger.debug("[WebrtcP2PEventHandler]   | getOrCreateContact found contact from phonenumber => contact id: " + foundContactByNumber.id);
                    return foundContactByNumber;
                }

                //create contact with received phonenumer and displayname in mediapillar data
                const contact = this.contactService.createBasicContact(null, callernumber);
                if (displayname) contact.displayName = displayname;
                return contact;
            }
            return this.contactService.getOrCreateContact(jid);
        }
        this.logger.info("[WebrtcP2PEventHandler]   | getOrCreateContact for jid " + jid);
        return this.contactService.getOrCreateContact(jid);
    }

    // On new proposition received
    private async onPropositionReceived(stanza: any): Promise<void> {
        const stanzaElem = xmlParser(stanza);
        const id = stanzaElem.find("propose").attr("id");
        const jid = stanzaElem.attr("from");
        this.logger.info("[WebrtcP2PEventHandler] jingle propose | onPropositionReceived for ID " + id + " and jid " + jid);
        let call: CallRB = this.webrtcP2PService.calls[id];

        try {
            if (!call) {
                const peerJid = this.xmppService.getBareJidFromJid(jid);
                call = CallRB.create(CallStatus.ENDED, id, CallType.WEBRTC, null);
                call.fullJid = jid;
                this.webrtcP2PService.calls[call.id] = call;
                call.contact = await this.getOrCreateContact(peerJid, stanzaElem);

                //CRRAINB-45813 exclude mediaPillar case to not disturb transfer case
                if (call.contact && this.webrtcP2PService.hasOtherWebrtcCallWithContactJid(call.id, call.contact.jid) && !this.contactService.isMediaPillarJid(call.fullJid)) {
                    // we're already in webrtc call with the same contact, reject this incoming call
                    // (usefull for specific usecase when 2 parties call each other in webrtc at the same time)
                    this.webrtcP2PService.terminateCall(call);
                    return;
                }

                // No more call => probably cleared by others client accept (because of the await)
                if (!this.webrtcP2PService.calls[call.id]) return;

                const mediaPillarElems = stanzaElem.find("mediapillar");
                if (mediaPillarElems.length > 0) {
                    const callIdElements = stanzaElem.find("callId");
                    const callId = callIdElements.text();
                    if (callId) {
                        this.logger.debug(`[WebrtcP2PEventHandler]   | onPropositionReceived mediapillar callId detected: ${callId}`);
                        call.setAssocPhoneCallId(callId);
                    }
                }
            }

            // Handle call subject
            const subjectElems = stanzaElem.find("subject");
            if (subjectElems.length > 0) {
                const subject = subjectElems.text();
                if (subject) {
                    this.logger.debug(`[WebrtcP2PEventHandler]   | onPropositionReceived subject Detected: ${subject}`);
                    call.setSubject(subject);
                }
            }

            if (call.remoteMedia === CallMedia.SHARING && call.contact && this.webrtcP2PService.hasMediaPillarCallWithContactJid(call.contact.jid)) {
                //we're in MP call with the same contact, mark this call as webrtc call on top of PBX call
                call.isSecondSharingCall = true;
            }

            // Set call status to RINGING
            call.setStatus(CallStatus.RINGING_INCOMMING);

            // Handle remote media
            const remoteMedia = [];
            const descriptionElems = stanzaElem.find("description");
            descriptionElems.forEach((description: XMLElement) => { remoteMedia.push(description.attr("media")); });
            this.setRemoteTypeMedia(remoteMedia, call);

            if (call.remoteMedia === CallMedia.SHARING && call.contact && this.webrtcP2PService.hasMediaPillarCallWithContactJid(call.contact.jid)) {
                //we're in MP call with the same contact, mark this call as webrtc call on top of PBX call
                call.isSecondSharingCall = true;
            }

            // Send call update event
            this.webrtcP2PService.sendUpdateCallEvent(call);
            this.webrtcP2PService.sendEvent(WebrtcP2PServiceEvents.RAINBOW_ON_CALL_UPDATED_EVENT, call);

            // Disable autoReleaseTimeout
            if (this.webrtcP2PService.autoreleaseTimeout) {
                clearTimeout(this.webrtcP2PService.autoreleaseTimeout);
            }

            if (!this.contactService.isMediaPillarJid(call.fullJid)) {
                // No timeout for mediapillar incoming calls
                this.webrtcP2PService.autoreleaseTimeout = setTimeout(() => {
                    if (call) {
                        this.logger.info("[WebrtcP2PEventHandler]   | onPropositionReceived autoreleaseTimeout after 2 min : remove the call " + call.id);
                        this.webrtcP2PService.removeCallObject(call);
                    }
                }, 120000);
            }
        }
        catch (error) {
            this.logger.error("[WebrtcP2PEventHandler]   | onPropositionReceived error " + error);
            if (this.webrtcP2PService.autoreleaseTimeout) {
                clearTimeout(this.webrtcP2PService.autoreleaseTimeout);
            }
            this.webrtcP2PService.removeCallObject(call);
        }
    }

    // On retract received
    private onRetractReceived(stanza: any): void {
        const id = xmlParser(stanza).find("retract").attr("id");

        this.logger.info("[WebrtcP2PEventHandler] jingle retract  | onRetractReceveid " + id);

        const call = this.webrtcP2PService.calls[id];

        if (call) {
            //if already answering the call, reset the online state
            if (call.callStatus === CallStatus.ANSWERING) {
                this.eventService.publish("CHECK_AND_RESET_BUSY_STATE");
            }

            this.webrtcP2PService.removeCallObject(call);
        }

        // Disable autoReleaseTimeout
        if (this.webrtcP2PService.autoreleaseTimeout) {
            clearTimeout(this.webrtcP2PService.autoreleaseTimeout);
        }
    }

    // One of my ressources has rejected the call
    private onRejectReceived(stanza: any): void {
        const stanzaElem = xmlParser(stanza);
        const id = stanzaElem.find("reject").attr("id");
        const call = this.webrtcP2PService.calls[id];
        const fullJid = stanzaElem.attr("from");
        const cause = stanzaElem.find("reject").attr("cause"); //RQRAINB-9062

        this.logger.info("[WebrtcP2PEventHandler] jingle reject | onRejectReceived " + id + " jid " + fullJid);

        // Disable autoReleaseTimeout
        if (this.webrtcP2PService.autoreleaseTimeout) {
            clearTimeout(this.webrtcP2PService.autoreleaseTimeout);
        }

        if (call) {
            //RQRAINB-9057 if call has been "dummy" rejected with cause "reserved"
            if (cause === "reserved") {
                if (call.getMediaPillarCall() !== null) { //webrtcGateway situation
                    if (this.contactService.userContact.fullJid === fullJid) { //rejected by myself
                        this.logger.debug("[WebrtcP2PEventHandler] jingle reject by myself| cause reserved then keep the call");
                        return; //DO NOT remove my reserved call
                    }
                    //else rejected by another ressource then ignore & remove de call
                    this.logger.debug("[WebrtcP2PEventHandler] jingle reject by other| cause reserved then give up the call");
                    this.eventService.publish("ON_MEDIAPILLAR_CALL_REMOTE_RESERVED_EVENT", call);
                    this.webrtcP2PService.removeCallObject(call);
                    return;
                }
            }

            //if call has been rejected by one of my other ressources
            if (call.callStatus === CallStatus.RINGING_INCOMMING) {
                if (call.getMediaPillarCall() !== null) { //webrtcGateway situation
                    //this.eventService.publish("ON_MEDIAPILLAR_CALL_REMOTE_CTRL_EVENT", call);
                    this.eventService.publish("ON_MEDIAPILLAR_CALL_REJECT_EVENT", call);
                }
                this.webrtcP2PService.removeCallObject(call);
            }

            //if call has been rejected by other party
            if (call.callStatus === CallStatus.DIALING) {
                this.eventService.publish("CHECK_AND_RESET_BUSY_STATE");
                this.webrtcP2PService.removeCallObject(call);
                if (cause === "busy") { //RQRAINB-9062
                    this.webrtcP2PService.sendEvent(WebrtcP2PServiceEvents.RAINBOW_ON_WEBRTC_BUSY);
                    this.logger.debug("[WebrtcP2PEventHandler] jingle reject | onRejectReceived cause busy");
                }
            }

            //ignore the call
            if (call.callStatus === CallStatus.ACTIVE || call.callStatus === CallStatus.CONNECTING) {
                //if reject from the same ressource that answered the call
                if (call.fullJid && call.fullJid === fullJid) {
                    this.eventService.publish("CHECK_AND_RESET_BUSY_STATE");
                    this.webrtcP2PService.removeCallObject(call);
                }
            }
        }
    }

    //one of my ressources has accepted the call
    private onAcceptReceived(stanza: any): void {
        const stanzaElem = xmlParser(stanza);
        const id = stanzaElem.find("accept").attr("id");
        const call = this.webrtcP2PService.calls[id];
        const fullJid = stanzaElem.attr("from");

        //if accepted call from another ressource
        if (call && fullJid !== this.contactService.userContact.fullJid) {
            this.logger.info("[WebrtcP2PEventHandler] jingle accept  | onAcceptReceived " + id + " jid " + fullJid);
            if (call.getMediaPillarCall() !== null) { //webrtcGateway situation
                this.eventService.publish("ON_MEDIAPILLAR_CALL_REMOTE_CTRL_EVENT", call);
            }
            this.webrtcP2PService.removeCallObject(call);
        }

        //if call was accepted on another device, cut the autorelease timeout
        if (fullJid !== this.contactService.userContact.fullJid && this.webrtcP2PService.autoreleaseTimeout) {
            clearTimeout(this.webrtcP2PService.autoreleaseTimeout);
        }
    }

    //the call has been accepted from the other participant
    private async onProceedReceived(stanza: any): Promise<void> {

        // Disable autoReleaseTimeout
        if (this.webrtcP2PService.autoreleaseTimeout) {
            clearTimeout(this.webrtcP2PService.autoreleaseTimeout);
        }

        const stanzaElem = xmlParser(stanza);
        const id = stanzaElem.find("proceed").attr("id");
        const call = this.webrtcP2PService.calls[id];
        const fullJid = stanzaElem.attr("from");

        this.logger.info("[WebrtcP2PEventHandler] jingle proceed  | onProceedReceived -- " + id + " jid " + fullJid);

        call.fullJid = fullJid;

        if (call.localMedia === CallMedia.SHARING && call.contact && this.webrtcP2PService.hasMediaPillarCallWithContactJid(call.contact.jid)) {
            //we're in MP call with the same contact, mark this call as webrtc call on top of PBX call
            call.isSecondSharingCall = true;
        }

        //if mobile answerred the call
        if (call.isInConversationWithMobile() && (call.localMedia & CallMedia.VIDEO)) {

            let mediaToGet = [];
            const mediaConstraints = this.webrtcConfigAndUtils.getMobileMediaConstraints("video");

            mediaToGet = ["audio", "video"];

            //if video only call (pbx case)
            if (!(call.localMedia & CallMedia.AUDIO)) {
                mediaToGet = ["video"];
            }
            try {
                let stream = await this.webrtcConfigAndUtils.getBrowserMedia(mediaToGet, mediaConstraints.fixedVideoWidth, mediaConstraints.fixedVideoHeight, mediaConstraints.fixedFrameRate);
                this.logger.info("[WebrtcP2PEventHandler]   | onProceedReceived --> renegociate video quality");

                if (this.webrtcP2PService.speechEnhancementService && (this.settingsService.getSetting('speechEnhancementType') !== "none" || this.settingsService.getSetting("enableSpeechEnhancement") === "true") && stream && stream.getAudioTracks().length) {
                    stream = await this.webrtcP2PService.speechEnhancementService.startProcessStream(call.id, stream, "[WebrtcP2PEventHandler] onProceedReceived");
                }

                if (call.localMedia & CallMedia.VIDEO) {
                    this.webrtcConfigAndUtils.disableMediaStreams(call.localStreams);
                    call.localStreams = [];
                    this.webrtcConfigAndUtils.disableMediaStream(call.originalStream);
                    call.originalStream = null;
                }

                let modifiedStream = stream;
                if (mediaToGet.indexOf("video") !== -1) {
                    call.originalStream = stream;
                    if (!this.webrtcP2PService.virtualBackgroundService) modifiedStream = stream;
                    else modifiedStream = await this.webrtcP2PService.virtualBackgroundService.startProcessStream(stream);
                }

                this.webrtcP2PService.pushInLocalStreams(modifiedStream, call.id);
                // this.webrtcP2PService.localStreams.push(modifiedStream);

                //make the jingle call
                this.webrtcP2PService.makeJingleCall(call);
            }
            catch (error) {
                this.logger.info("[WebrtcP2PEventHandler]   | onProceedReceived " + call + " failure : " + error.message);

                if (error.toString().indexOf("getBrowserMedia") !== -1) {
                    //error when trying to get camera stream
                    this.logger.info("onProceedReceived impossible, no webcam plugged");
                    this.webrtcP2PService.openErrorPopup();
                }
                else {
                    //Another kind of error will lead to call end
                    this.webrtcP2PService.releaseCall(call);
                    this.webrtcP2PService.resetToSafeState();
                }
            }
        }
        else {
            if (call.localMedia & CallMedia.SHARING) {
                this.webrtcP2PService.makeJingleSharingCall(call);
            } else {
                //make the jingle call
                this.webrtcP2PService.makeJingleCall(call);
            }
        }
    }

    /*************************************************************/
    /* CALL EVENT HANDLERS                                     */
    /*************************************************************/

    // Incomming call event handler
    public async incomingCall(callId: string) {

        const sess = this.xmppService.connection.jingle.sessions[callId];
        if (sess.localType === 'application' || sess.remoteType === 'application') return;
        this.logger.info("[WebrtcP2PEventHandler] incomingCall " + callId);

        let call = null;
        if (sess && sess.peerjid) {
            call = this.webrtcP2PService.calls[callId];
            if (!call) {
                // Get associated contact
                const peerJid = this.xmppService.getBareJidFromJid(sess.peerjid);
                const contact = this.contactService.getContactFromCacheByJid(peerJid);
                call = this.webrtcP2PService.getCallByJid(contact.jid, sess.peerjid);
            }
        }

        if (call) {
            sess.sendRinging();

            if (this.webrtcP2PService.autoreleaseTimeout) {
                clearTimeout(this.webrtcP2PService.autoreleaseTimeout);
            }

            await this.webrtcP2PService.answerJingleCall(call);

            if (call.statsInterval) {
                this.logger.info("[WebrtcP2PEventHandler] remove getStats interval");

                window.clearInterval(call.statsInterval);
                call.statsInterval = null;
            }

            this.webrtcConfigAndUtils.createOrUpdateStatisticsForCall(call);

            this.logger.info("[WebrtcP2PEventHandler] start getStats interval");
            call.statsInterval = this.webrtcConfigAndUtils.getStatsForSession(sess, 5000);

        } else {
            this.logger.warn("[WebrtcP2PEventHandler] incomingCall no such call ! Terminate the session ");
            this.eventService.publish("CHECK_AND_RESET_BUSY_STATE");

            if (sess) {
                sess.terminate();
            }
        }
    }

    // Active video call event handler
    public activeCall(callId) {
        const sess = this.xmppService.connection.jingle.sessions[callId];

        // Disable autoReleaseTimeout
        if (this.webrtcP2PService.autoreleaseTimeout) {
            clearTimeout(this.webrtcP2PService.autoreleaseTimeout);
        }

        const call = this.webrtcP2PService.calls[callId];

        this.webrtcP2PService.updateRemoteTypeMedia(sess.remoteType, call);

        if (call && call.callStatus !== CallStatus.RELEASING && call.callStatus !== CallStatus.ENDED) {
            call.setStatus(CallStatus.ACTIVE);
            this.webrtcP2PService.sendUpdateCallEvent(call);
            call.startDuration();
            this.webrtcP2PService.attachDistantMediaStreams(true, call);

            //send call media updated event in case there's local/remote video or sharing
            if (call.localMedia !== CallMedia.AUDIO || call.remoteMedia !== CallMedia.AUDIO) {
                call.sendEvent(CallEvents.ON_CALL_MEDIA_UPDATED);
            }

            this.webrtcP2PService.sendEvent(WebrtcP2PServiceEvents.RAINBOW_ON_CALL_UPDATED_EVENT);
        }
    }

    // Terminate video call event handler
    public terminatedCall(callId, callReason = "", doNotUpdatePresence: boolean = false) {
        if (Object.keys(this.xmppService.connection.jingle.sessions).length === 0) {
            this.logger.info("INFO    | all calls terminated");
        }

        this.logger.info("[WebrtcP2PEventHandler]  | callterminated " + callId + " reason: " + callReason);
        const call = this.webrtcP2PService.calls[callId];

        if (callReason === "expired") {
            this.webrtcP2PService.sendEvent(WebrtcP2PServiceEvents.RAINBOW_SUPERVISION_SNAPSHOT_NEEDED);
        }

        //callSwitch adaptation++
        if (callReason === "switchDevice") {
            this.logger.info("[WebrtcP2PEventHandler]  | callSwitch for " + callId + " then becomes remoteControled");
            this.eventService.publish("ON_MEDIAPILLAR_CALL_REMOTE_CTRL_EVENT", call);
        }//callSwitch adaptation--

        if (!call || callReason === "multi-device") {
            //do nothing
            return;
        }

        //if the terminated call does not exist, but we still have other calls, do nothing
        if (!call && Object.keys(this.webrtcP2PService.calls).length !== 0) {
            this.logger.info("INFO    | terminatedCall : no call with " + callId + " exists.");
            return;
        }

        if (callReason === "busy") {
            this.webrtcP2PService.openErrorPopup("remoteUserNoMoreAvailable");
        }

        // Disable autoReleaseTimeout
        if (this.webrtcP2PService.autoreleaseTimeout) {
            clearTimeout(this.webrtcP2PService.autoreleaseTimeout);
        }

        //release media resources
        this.webrtcConfigAndUtils.disableAllStreamsForSession(call); //session is null because it does not exist anymore (removed by strophe layer)

        this.webrtcConfigAndUtils.createOrUpdateStatisticsForCall(call);

        if (this.webrtcConfigAndUtils.callsStats[callId]) {
            delete this.webrtcConfigAndUtils.callsStats[callId];
        }

        if (call) {
            this.webrtcP2PService.removeCallObject(call);
        }

        if (!doNotUpdatePresence) {
            setTimeout(() => {
                //wait 1 sec, because sometimes the busy presence arrives later and the user stays blocked in busy state
                //because his presence state is online at that exact moment
                this.eventService.publish("CHECK_AND_RESET_BUSY_STATE");
            }, 1000);
        }

        // Disable autoReleaseTimeout
        if (call && call.statsInterval) {
            window.clearInterval(call.statsInterval);
            call.statsInterval = null;
        }

        if (!this.webrtcP2PService.isUserContactInCall()) {
            this.webrtcP2PService.clearSrcObjectsFromElements();
        }
    }

    public onJingleError(sid, error: any) {
        const session = this.xmppService.connection.jingle.sessions[sid];
        if (session && (session.localType === 'application' || session.remoteType === 'application')) return;
        this.logger.error("[WebrtcP2PEventHandler]  | on jingle callerror");

        if (error) {
            this.logger.error(JSON.stringify(error));
        }

        if (sid && this.webrtcP2PService.calls[sid] && this.webrtcP2PService.calls[sid].callStatus !== CallStatus.ACTIVE) {
            this.webrtcP2PService.removeCallObject(this.webrtcP2PService.calls[sid]);
            this.webrtcP2PService.resetToSafeState();
            this.webrtcP2PService.openErrorPopup("IceConnectionFailed");
        }
    }

    // ICE state change event handler
    public onSessionConnectionStateUpdate(sid: string, sess: JingleSession) {
        let activeCall: CallRB;
        const call = this.webrtcP2PService.calls[sess.sid];

        if (!call) {
            //do nothing
            return;
        }

        if (!sess.peerconnection) {
            this.logger.info(`[WebrtcP2PEventHandler] onSessionConnectionStateUpdate for ${sid} -- peerConnection does not exist anymore`);
            return;
        }

        try {
            const state = sess.peerconnection.connectionState ? sess.peerconnection.connectionState : sess.peerconnection.iceConnectionState;

            if (sess.peerconnection.signalingState !== "stable") {
                if (state === "closed" || sess.peerconnection.signalingState === "closed") {
                    this.logger.info(`[WebrtcP2PEventHandler] onSessionConnectionStateUpdate for ${sid} -- state is ended/closed, remove the call`);
                    this.webrtcP2PService.removeCallObject(call);
                    this.webrtcP2PService.resetToSafeState();
                    return;
                }
                this.logger.info(`[WebrtcP2PEventHandler] onSessionConnectionStateUpdate for ${sid}: signalingState ${sess.peerconnection.signalingState} iceConnectionState ${sess.peerconnection.iceConnectionState} and connectionState ${sess.peerconnection.connectionState} -- event is ignored as signalingState is not stable`);
                return;
            }

            this.logger.info(`[WebrtcP2PEventHandler] onSessionConnectionStateUpdate for ${sid}: signalingState ${sess.peerconnection.signalingState} | iceConnectionState ${sess.peerconnection.iceConnectionState} | connectionState ${sess.peerconnection.connectionState}`);

            if (sess.peerconnection.iceConnectionState === "checking") {
                //add timer to check for blocked state
                setTimeout(() => {
                    if (sess?.peerconnection?.iceConnectionState === "checking") {
                        //call is blocked in checking state, we need to trigger ICE restart
                        this.logger.debug(`[WebrtcP2PEventHandler] onSessionConnectionStateUpdate for ${sid} -- session is blocked with iceConnectionState ${sess.peerconnection.iceConnectionState}`);
                        sess.reconnectSession();
                    }
                }, 4000);
            }

            if (sess.peerconnection.connectionState !== "connected" && (sess.peerconnection.iceConnectionState === "connected" || sess.peerconnection.iceConnectionState === "completed")) {
                setTimeout(() => {
                    if (sess?.peerconnection?.connectionState !== "connected" && (sess?.peerconnection?.iceConnectionState === "connected" || sess?.peerconnection?.iceConnectionState === "completed")) {
                        //call is blocked in connecting DTLS state, we need to trigger ICE restart
                        this.logger.debug(`[WebrtcP2PEventHandler] onSessionConnectionStateUpdate for ${sid} -- session is blocked in iceConnectionState ${sess.peerconnection.iceConnectionState} and connectionState ${sess.peerconnection.connectionState} (DTLS timeout)`);
                        sess.reconnectSession();
                    }
                }, 3000);
            }

            if (state === "connected" || state === "completed") {
                activeCall = this.webrtcP2PService.calls[sid];

                if (state === "completed" && activeCall.callStatus === CallStatus.ACTIVE) return;

                this.logger.info(`[WebrtcP2PEventHandler] onSessionConnectionStateUpdate for ${sid} -- call established successfully`);

                const globalAudioTagElement: any = document.getElementById("globalAudioTag");
                if (globalAudioTagElement) {
                    this.logger.info("[WebrtcP2PEventHandler] sinkId for globalAudioTag is " + globalAudioTagElement["sinkId"]);
                }

                this.webrtcP2PService.reconnectCall = false;

                if (activeCall && activeCall.onHold) {
                    //call is reconnected successfully should put in back in hold state
                    call.setStatus(CallStatus.HOLD);
                }
                else if (activeCall && activeCall.putOnHold) {
                    call.setStatus(CallStatus.PUT_ON_HOLD);
                }
                else if (activeCall && activeCall.callStatus !== CallStatus.ACTIVE) {
                    this.logger.info(`[WebrtcP2PEventHandler] onSessionConnectionStateUpdate -- call ${sid} is now ACTIVE`);
                    this.activeCall(sid);
                }

                //if renegotiation
                if (sess.isRenegotiating) {
                    this.logger.info(`[WebrtcP2PEventHandler] call ${sid} -- Renegotiating done`);
                    sess.isRenegotiating = false;
                    this.webrtcP2PService.sendEvent(WebrtcP2PServiceEvents.RAINBOW_ON_CALL_UPDATED_EVENT);
                }

                //disable timer
                if (sess.autoReconnectTimer) {
                    clearTimeout(sess.autoReconnectTimer);
                    sess.autoReconnectTimer = null;
                }

                // Disable autoReleaseTimeout
                if (this.webrtcP2PService.autoreleaseTimeout) {
                    clearTimeout(this.webrtcP2PService.autoreleaseTimeout);
                    this.webrtcP2PService.autoreleaseTimeout = null;
                }
            }

            if (sess.peerconnection.iceConnectionState === "disconnected" || sess.peerconnection.iceConnectionState === "failed") {
                this.logger.info(`[WebrtcP2PEventHandler] onSessionConnectionStateUpdate for ${sid} -- call is ${sess.peerconnection.iceConnectionState}, start reconnect mecanism`);

                if (call && call.callStatus !== CallStatus.ENDED) {
                    call.setStatus(CallStatus.CONNECTING);
                    this.webrtcP2PService.sendUpdateCallEvent(call);
                }

                if (!this.webrtcP2PService.connected) {
                    this.logger.debug(`[WebrtcP2PEventHandler] onSessionConnectionStateUpdate for ${sid} -- network is DOWN, wait before reconnect`);
                    this.webrtcP2PService.reconnectCall = true;
                }

                if (this.webrtcP2PService.connected) {
                    if (sess.peerconnection.iceConnectionState === "disconnected") {
                        if (!sess.autoReconnectTimer) {
                            this.logger.debug(`[WebrtcP2PEventHandler] onSessionConnectionStateUpdate for ${sid} -- ICE disconnected, arm timeout`);
                            sess.autoReconnectTimer = setTimeout(() => {
                                this.logger.debug(`[WebrtcP2PEventHandler] onSessionConnectionStateUpdate for ${sid} -- timeout expire, check network state`);
                                if (this.webrtcP2PService.connected && (sess?.peerconnection?.iceConnectionState === "disconnected" || sess?.peerconnection?.iceConnectionState === "failed")) {
                                    this.logger.debug(`[WebrtcP2PEventHandler] onSessionConnectionStateUpdate for ${sid} -- network OK, proceed with reconnect`);
                                    sess.reconnectSession();
                                }
                            }, 2000);
                        }
                    } else {
                        this.logger.debug(`[WebrtcP2PEventHandler] onSessionConnectionStateUpdate for ${sid} -- network OK, proceed with reconnect`);
                        sess.reconnectSession();
                    }
                }

                //set autoreleaseTimeout
                if (!this.webrtcP2PService.autoreleaseTimeout) {
                    this.webrtcP2PService.autoreleaseTimeout = setTimeout(() => {
                        this.logger.info(`[WebrtcP2PEventHandler] onSessionConnectionStateUpdate for ${sid} -- reconnection is impossible after 45 seconds : release the call`);
                        if (call && call.callStatus !== CallStatus.ENDED) {
                            this.webrtcP2PService.terminateCall(call);
                        }
                    }, 45000);
                }
            }
        } catch (error) {
            try {
                this.logger.error(`[WebrtcP2PEventHandler] onSessionConnectionStateUpdate for ${sid} error -- ${stringifyError(error)}`);
                if (call) {
                    this.webrtcP2PService.removeCallObject(call, "unsupported-applications");
                } else {
                    this.xmppService.connection.jingle.terminate(null, "unsupported-applications");
                }

                this.eventService.publish("CHECK_AND_RESET_BUSY_STATE");
            } catch (err) {
                this.logger.error(`[WebrtcP2PEventHandler] onSessionConnectionStateUpdate for ${sid} final error... ${(stringifyError(err))}`);
                this.eventService.publish("CHECK_AND_RESET_BUSY_STATE");
            }
        }
    }

    // media modified call event handler
    public mediaModified(sid) {
        this.logger.info("[WebrtcP2PEventHandler] mediaModified " + sid);

        const call = this.webrtcP2PService.calls[sid];
        const sess = this.xmppService.connection.jingle.sessions[sid];

        if (!call) {
            //ignore
            return;
        }

        call.remoteMedia = 0;
        if (sess.remoteStreamsObject[0]) {
            call.remoteMedia = CallMedia.AUDIO;
        }
        if (sess.remoteStreamsObject[1]) {
            call.remoteMedia = call.remoteMedia | CallMedia.VIDEO;
        }
        if (sess.remoteStreamsObject[2]) {
            call.remoteMedia = call.remoteMedia | CallMedia.SHARING;
        }

        if (!sess.remoteStreamsObject[3]) {
            call.hasRemoteSharingWithAudio = false;
        }

        this.contactService.setBusyState("dnd", this.webrtcP2PService.calculatePresenceMessage(call));

        this.logger.info(`[WebrtcP2PEventHandler] mediaModified ${JSON.stringify(call.remoteMedia)}`);

        call.sendEvent(CallEvents.ON_CALL_MEDIA_UPDATED);
    }

    public async onTrasferCallEvent(referTo: string, otherSid: string) {
        this.logger.info(`[WebrtcP2PEventHandler]   | onTrasferCallEvent referTo ${referTo} and other sid ${otherSid}`);
        const mediaToGet = ["audio"];
        const contact = await this.contactService.getOrCreateContact(this.xmppService.getBareJidFromJid(referTo));

        const id = "web_" + crypto.randomUUID();

        // Create the call object
        const call = CallRB.create(CallStatus.DIALING, id, CallType.WEBRTC, contact);
        let stream;
        try {
            stream = await this.webrtcConfigAndUtils.getBrowserMedia(mediaToGet, undefined, undefined, undefined, call);

            this.webrtcP2PService.calls[call.id] = call;
            call.isInitiator = true;
            stream.callId = id;
            call.fullJid = referTo;

            if (this.webrtcP2PService.speechEnhancementService && (this.settingsService.getSetting('speechEnhancementType') !== "none" || this.settingsService.getSetting("enableSpeechEnhancement") === "true") && stream && stream.getAudioTracks().length) {
                stream = await this.webrtcP2PService.speechEnhancementService.startProcessStream(call.id, stream, "[WebrtcP2PEventHandler] onTrasferCallEvent");
            }

            this.webrtcP2PService.pushInLocalStreams(stream, id);

            this.setLocalTypeMedia(mediaToGet, call);

            await this.webrtcP2PService.makeJingleCall(call, null, null, otherSid);

            // Update presence
            this.contactService.setBusyState("dnd", this.webrtcP2PService.calculatePresenceMessage(call));

            this.eventService.publish("ON_WEBRTC_CALL_TRANSFER_EVENT", contact.jid);
        }
        catch (error) {
            this.logger.info("[WebrtcP2PEventHandler]   | onTrasferCallEvent failure : " + JSON.stringify({ error: error.message }));

            //call is already made
            this.webrtcP2PService.makingCall = false;

            this.webrtcP2PService.removeCallObject(call);

            if (!this.webrtcP2PService.isUserContactInCall()) {
                this.webrtcP2PService.resetToSafeState();
            }

            if (this.webrtcP2PService.autoreleaseTimeout) {
                clearTimeout(this.webrtcP2PService.autoreleaseTimeout);
            }

            if (stream) {
                stream.getTracks().forEach(function (track) {
                    track.enabled = false;
                    track.stop();
                    track = null;
                });

                if (stream.stop) {
                    stream.stop();
                }

                stream = null;
            }
        }
    }

    public async onIncomingTrasferredCall(sid: string) {
        this.logger.info(`[WebrtcP2PEventHandler]   | onIncomingTransferredCall for sid ${sid}`);
        //on new proposition received
        const sess = this.xmppService.connection.jingle.sessions[sid];
        const peerJid = this.xmppService.getBareJidFromJid(sess.peerjid);
        const call = CallRB.create(CallStatus.CONNECTING, sid, CallType.WEBRTC, null);
        try {
            call.fullJid = sess.peerjid;
            this.webrtcP2PService.calls[call.id] = call;

            const contact = await this.contactService.getOrCreateContact(peerJid);
            call.contact = contact;

            const remoteMedia = ["audio"];
            this.setRemoteTypeMedia(remoteMedia, call);
            call.localMedia = CallMedia.AUDIO;

            this.webrtcP2PService.sendUpdateCallEvent(call);
            this.incomingCall(sid);

            this.eventService.publish("ON_WEBRTC_CALL_TRANSFER_EVENT", peerJid);
        }
        catch (error) {
            this.logger.error("[WebrtcP2PEventHandler]   | onIncomingTransferredCall error " + error);
            this.webrtcP2PService.removeCallObject(call);
        }
    }

    public async onIncomingShiftCall(oldCallId: string, newCallId: string) {
        this.logger.info(`[WebrtcP2PEventHandler]   | onIncomingShiftCall for old sid ${oldCallId} to ${newCallId}`);

        const oldCall = this.webrtcP2PService.calls[oldCallId];
        let sharingStream;
        let addVideo = false;
        let shouldMute = false;
        const mediaToGet = ["audio"];
        const remoteMedia = ["audio"];

        //get the medias in the old call
        if (oldCall) {
            shouldMute = oldCall.isMuted;
            const oldSession = this.xmppService.connection.jingle.sessions[oldCall.id];
            try {
                if (oldSession?.localType && oldSession.localType.indexOf("sharing") !== -1) {
                    let index = oldSession.localStreams.length - 1;
                    //in case we've started with sharing and then added audio, get the right index
                    if (oldSession.localStreams[oldSession.localStreams.length - 1].getAudioTracks().length) {
                        index = 0;
                    }
                    sharingStream = oldSession.localStreams[index].clone();
                    sharingStream["mediaStreamKind"] = oldSession.localStreams[index]["mediaStreamKind"];
                }

                if (oldSession?.localType && oldSession.localType.indexOf("video") !== -1) {
                    addVideo = true;
                }

                if (oldSession?.remoteType && oldSession.remoteType.indexOf("video") !== -1) {
                    remoteMedia.push("video");
                }
            } catch (err) {
                this.logger.error("[WebrtcP2PEventHandler] onIncomingShiftCall error on duplicate stream -- " + err);
                return;
            }

            //remove old call
            this.webrtcP2PService.removeCallObject(oldCall);
        }

        //accept new call and add media
        const session = this.xmppService.connection.jingle.sessions[newCallId];
        const peerJid = this.xmppService.getBareJidFromJid(session.peerjid);
        const call = CallRB.create(CallStatus.CONNECTING, newCallId, CallType.WEBRTC, null);
        call.fullJid = session.peerjid;
        this.webrtcP2PService.calls[call.id] = call;
        call.contact = await this.contactService.getOrCreateContact(peerJid);
        this.setRemoteTypeMedia(remoteMedia, call);
        this.setLocalTypeMedia(mediaToGet, call);

        this.webrtcP2PService.sendUpdateCallEvent(call);

        //if in conversation with mobile, limit the video quality
        let mediaConstraints: any = {};
        if (call.isInConversationWithMobile()) {
            mediaConstraints = this.webrtcConfigAndUtils.getMobileMediaConstraints("video");
        }
        try {
            await this.webrtcConfigAndUtils.setWebrtcConfiguration();
            let stream = await this.webrtcConfigAndUtils.getBrowserMedia(mediaToGet, mediaConstraints.fixedVideoWidth, mediaConstraints.fixedVideoHeight, mediaConstraints.fixedFrameRate);

            if (this.webrtcP2PService.speechEnhancementService && (this.settingsService.getSetting('speechEnhancementType') !== "none" || this.settingsService.getSetting("enableSpeechEnhancement") === "true") && stream && stream.getAudioTracks().length) {
                stream = await this.webrtcP2PService.speechEnhancementService.startProcessStream(call.id, stream, "[WebrtcP2PEventHandler] onIncomingShiftCall");
            }

            if (!session) {
                //session is already destroyed, cut the streams and close the call
                if (stream) { this.webrtcP2PService.disableStreams([stream]); }
                if (sharingStream) { this.webrtcP2PService.disableStreams([sharingStream]); }
                this.webrtcP2PService.releaseCall(call);
                this.webrtcP2PService.resetToSafeState(call);
                return;
            }

            if (stream) {
                let modifiedStream = stream;
                if (mediaToGet.indexOf("video") !== -1) {
                    call.originalStream = stream;
                    modifiedStream = this.webrtcP2PService.virtualBackgroundService ? await this.webrtcP2PService.virtualBackgroundService.startProcessStream(stream) : stream;
                }
                this.webrtcP2PService.pushInLocalStreams(modifiedStream, call.id);

                session.localStreams.push(modifiedStream);
                await session.updateSessionStreamsAndTransceivers(call.localStreams);
            }

            session.sendAnswer();

            this.webrtcP2PService.sendUpdateCallEvent(call);
            this.eventService.publish("ON_WEBRTC_CALL_TRANSFER_EVENT", peerJid);

            //mute call
            if (shouldMute) {
                call.mute();
            }

            if (addVideo || sharingStream) {
                setTimeout(() => {
                    if (sharingStream) {
                        //add sharing stream if available
                        sharingStream.callId = call.id;
                        this.webrtcP2PService.pushInLocalStreams(sharingStream, call.id);
                        this.setLocalTypeMedia(["audio", "sharing"], call);
                        this.webrtcP2PService.sendEvent("startHighlighting");
                    }

                    if (addVideo) {
                        //the add video will also update the sharing that we've added
                        call.addVideo();
                    }
                    else {
                        //otherwise update the call to add sharing
                        this.webrtcP2PService.updateCurrentCall(call, session, "sharing", "contentAdd");
                    }
                }, 300);
            }
        }
        catch (error) {
            this.logger.info("[webrtcP2PService] MEDIA   | onIncomingShiftCall failure for : " + call.id + " failure : " + error.message);

            if (error.toString()?.indexOf("getBrowserMedia") !== -1) {
                this.webrtcP2PService.releaseCall(call);
                //error when trying to get camera stream
                this.webrtcP2PService.openErrorPopup();
            }
            this.webrtcP2PService.resetToSafeState(call);
        }
    }
}
